import { infiniteQueryOptions, queryOptions } from "@tanstack/react-query";
import { authenticatedRequest } from "@/lib/graphql/client";
import {
  ADMIN_USER_CONVERSATIONS,
  ADMIN_USER_CONVERSATION_MESSAGES,
} from "@/lib/graphql/queries/user-conversations";
import type { PaginationInput } from "@/types/common";
import type {
  ConversationMessagesPage,
  UserConversationsResult,
} from "@/types/user-conversation";

const MESSAGES_PAGE_SIZE = 30;

export const userConversationKeys = {
  all: ["userConversations"] as const,
  lists: () => [...userConversationKeys.all, "list"] as const,
  list: (userId: string, pagination: PaginationInput) =>
    [...userConversationKeys.lists(), userId, pagination] as const,
  messages: () => [...userConversationKeys.all, "messages"] as const,
  session: (sessionId: string) =>
    [...userConversationKeys.messages(), sessionId] as const,
};

export function userConversationsQueryOptions(
  userId: string | null,
  pagination: PaginationInput
) {
  return queryOptions({
    queryKey: userConversationKeys.list(userId ?? "", pagination),
    queryFn: async () => {
      const data = await authenticatedRequest<{
        adminUserConversations: UserConversationsResult;
      }>(ADMIN_USER_CONVERSATIONS, {
        input: { userId, page: pagination.page, limit: pagination.limit },
      });
      return data.adminUserConversations;
    },
    enabled: Boolean(userId),
    placeholderData: (previousData) => previousData,
    staleTime: 30000,
  });
}

export function userConversationMessagesInfiniteOptions(
  sessionId: string | null
) {
  return infiniteQueryOptions({
    queryKey: userConversationKeys.session(sessionId ?? ""),
    queryFn: async ({ pageParam }) => {
      const data = await authenticatedRequest<{
        adminUserConversationMessages: ConversationMessagesPage;
      }>(ADMIN_USER_CONVERSATION_MESSAGES, {
        input: {
          sessionId,
          limit: MESSAGES_PAGE_SIZE,
          cursor: pageParam ?? undefined,
        },
      });
      return data.adminUserConversationMessages;
    },
    enabled: Boolean(sessionId),
    initialPageParam: null as string | null,
    getNextPageParam: (lastPage) =>
      lastPage.hasMore ? lastPage.nextCursor : undefined,
    staleTime: 60000,
  });
}
